import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Scale, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { useScaleStore, isLiveScaleEnabled, isScaleBarcodeEnabled } from '@/store/scaleStore';
import { useScaleConnection } from '@/hooks/useScaleConnection';
import {
  SCALE_BRAND_PRESETS,
  SCALE_PROTOCOL_OPTIONS,
  type ScaleBrandPreset,
  type ScaleProtocol,
} from '@/utils/scaleProtocols';
import {
  parseScaleBarcode,
  type WeightInputMethod,
} from '@/utils/scaleBarcode';
import { posApi } from '@/services/api';
import { isDesktopApp, getElectronAPI } from '@/desktop/electronApi';
import { formatMoney } from '@/utils/currency';

const SELECT_CLS =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900';

const METHOD_OPTIONS: Array<{
  id: WeightInputMethod;
  title: string;
  description: string;
}> = [
  {
    id: 'live',
    title: 'Live weighing scale',
    description: 'Read weight directly from a connected USB / RS-232 scale.',
  },
  {
    id: 'barcode',
    title: 'Printed scale barcode',
    description: 'Scan barcode labels printed by a weighing scale.',
  },
  {
    id: 'both',
    title: 'Both',
    description: 'Use both connected scales and printed scale labels.',
  },
];

type BarcodeTestResult =
  | { kind: 'idle' }
  | { kind: 'invalid_checksum' }
  | { kind: 'not_recognized' }
  | {
      kind: 'ok';
      plu: string;
      weight: number;
      unit: string;
      productName?: string;
      rate?: number;
      amount?: number;
      unmapped?: boolean;
      error?: string;
    };

export default function ScaleSettingsPage() {
  const { t } = useTranslation();
  const {
    draft,
    isDirty,
    errorMsg,
    lastWeightKg,
    updateDraft,
    updateDraftProfile,
    applyBrandPreset,
    saveDraft,
    discardDraft,
    resetDefaults,
    setLastWeightKg,
  } = useScaleStore();

  const {
    status: connStatus,
    listPorts,
    requestWebPort,
    testConnection,
    disconnect,
    transport,
  } = useScaleConnection();

  const [ports, setPorts] = useState<Array<{ id: string; label: string; path?: string }>>([]);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [testing, setTesting] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [testCode, setTestCode] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<BarcodeTestResult>({ kind: 'idle' });
  const desktop = isDesktopApp();

  const showLive = isLiveScaleEnabled(draft);
  const showBarcode = isScaleBarcodeEnabled(draft);

  useEffect(() => {
    void listPorts().then(setPorts);
  }, [listPorts]);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const handleSave = async () => {
    saveDraft();
    if (desktop && showLive) {
      try {
        await getElectronAPI()?.setConfig?.({
          scalePath: draft.path,
          scaleBaudRate: draft.baudRate,
          scaleProtocol: draft.protocol,
        } as never);
      } catch {
        /* optional desktop config mirror */
      }
    }
    showToast(t('settings.scaleSaved', { defaultValue: 'Scale settings saved.' }), true);
  };

  const handleTest = async () => {
    setTesting(true);
    setLastWeightKg(null);
    const res = await testConnection();
    setTesting(false);
    showToast(
      res.ok
        ? t('settings.scaleConnected', {
            defaultValue: 'Connected — place weight on the scale.',
          })
        : res.error || 'Connection failed.',
      res.ok,
    );
  };

  const handleTestBarcode = async () => {
    const code = testCode.trim();
    if (!code) return;
    setTestBusy(true);
    setTestResult({ kind: 'idle' });
    const parsed = parseScaleBarcode(code, draft.scaleBarcodeProfile);
    if (!parsed.ok) {
      setTestBusy(false);
      if (parsed.reason === 'checksum') {
        setTestResult({ kind: 'invalid_checksum' });
      } else {
        setTestResult({ kind: 'not_recognized' });
      }
      return;
    }

    try {
      const res = await posApi.scanBarcode(code, {
        enableScaleBarcode: true,
        profile: draft.scaleBarcodeProfile,
      });
      const data = (res as { data?: { message?: Record<string, unknown> } })?.data?.message
        || (res as { message?: Record<string, unknown> })?.message
        || res;
      const msg = data as Record<string, unknown>;
      const rate = Number(msg.rate ?? 0);
      const qty = Number(msg.quantity ?? parsed.weight ?? 0);
      setTestResult({
        kind: 'ok',
        plu: parsed.scalePluPadded || parsed.scalePlu || '',
        weight: parsed.weight || 0,
        unit: parsed.weightUnit || 'kg',
        productName: String(msg.item_name || ''),
        rate,
        amount: rate * qty,
      });
    } catch (err: unknown) {
      const serverMsg =
        (err as { response?: { data?: { message?: string; _server_messages?: string } } })?.response
          ?.data?.message
        || (err as { message?: string })?.message
        || '';
      const unmapped = /Scale PLU|no product/i.test(String(serverMsg));
      setTestResult({
        kind: 'ok',
        plu: parsed.scalePluPadded || parsed.scalePlu || '',
        weight: parsed.weight || 0,
        unit: parsed.weightUnit || 'kg',
        unmapped,
        error: String(serverMsg || 'Lookup failed'),
      });
    } finally {
      setTestBusy(false);
    }
  };

  const needsSerial = showLive && draft.protocol !== 'keyboard_wedge' && draft.brandPreset !== 'none';

  const methodHint = useMemo(() => {
    if (draft.weightInputMethod === 'barcode') {
      return t('settings.scaleHintBarcode', {
        defaultValue: 'Scan printed scale labels at POS. No USB/serial connection required.',
      });
    }
    if (draft.weightInputMethod === 'both') {
      return t('settings.scaleHintBoth', {
        defaultValue:
          'Live USB/RS-232 scale and printed scale barcode labels are both enabled.',
      });
    }
    return t('settings.scaleHintWeb', {
      defaultValue:
        'Live weight in POS via USB/RS-232 (Chrome/Edge Web Serial or desktop app). Manual kg always available.',
    });
  }, [draft.weightInputMethod, t]);

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Scale className="w-5 h-5" />
            {t('settings.weighingScale', { defaultValue: 'Weighing Scale' })}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{methodHint}</p>
        </div>
        <div className="flex items-center gap-2">
          {showLive && (
            <span
              className={`text-xs px-2.5 py-1 rounded-md border font-medium ${
                connStatus === 'connected'
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : connStatus === 'connecting'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : connStatus === 'error'
                      ? 'bg-red-50 text-red-700 border-red-200'
                      : 'bg-gray-50 text-gray-500 border-gray-200'
              }`}
            >
              {connStatus === 'connected'
                ? 'Connected'
                : connStatus === 'connecting'
                  ? 'Connecting…'
                  : connStatus === 'error'
                    ? 'Error'
                    : 'Not connected'}
            </span>
          )}
          <span
            className={`text-xs px-2.5 py-1 rounded-md border font-medium ${
              isDirty
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : 'bg-green-50 text-green-700 border-green-200'
            }`}
          >
            {isDirty ? 'Unsaved' : 'Saved'}
          </span>
        </div>
      </div>

      {toast && (
        <div
          className={`text-sm px-4 py-3 rounded-lg border ${
            toast.ok
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Weight input method */}
      <section className="border border-gray-200 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-medium text-gray-900 pb-2 border-b border-gray-100">
          {t('settings.weightInputMethod', { defaultValue: 'Weight input method' })}
        </h2>
        <div className="space-y-2">
          {METHOD_OPTIONS.map((opt) => {
            const selected = draft.weightInputMethod === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => updateDraft('weightInputMethod', opt.id)}
                className={`w-full text-start rounded-lg border px-3 py-3 transition-colors ${
                  selected
                    ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center shrink-0 ${
                      selected ? 'border-gray-900' : 'border-gray-300'
                    }`}
                  >
                    {selected ? <span className="h-2 w-2 rounded-full bg-gray-900" /> : null}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-gray-900">{opt.title}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{opt.description}</span>
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {showLive && transport.available === false && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          {transport.note}
        </p>
      )}

      {showLive && transport.available && (
        <p className="text-xs text-gray-500">
          Transport: {transport.mode === 'electron' ? 'Desktop serial' : 'Web Serial'} — {transport.note}
        </p>
      )}

      {showLive && (
        <>
          {/* Brand */}
          <section className="border border-gray-200 rounded-xl p-5 space-y-3">
            <h2 className="text-sm font-medium text-gray-900 pb-2 border-b border-gray-100">
              {t('settings.scaleBrand', { defaultValue: 'Scale / brand' })}
            </h2>
            <label className="block">
              <span className="text-xs text-gray-500">Preset</span>
              <select
                className={`${SELECT_CLS} mt-1`}
                value={draft.brandPreset}
                onChange={(e) => applyBrandPreset(e.target.value as ScaleBrandPreset)}
              >
                {SCALE_BRAND_PRESETS.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-gray-500">{t('settings.scaleProtocol')}</span>
              <select
                className={`${SELECT_CLS} mt-1`}
                value={draft.protocol}
                onChange={(e) => updateDraft('protocol', e.target.value as ScaleProtocol)}
              >
                {SCALE_PROTOCOL_OPTIONS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            {draft.brandPreset === 'dibal' && (
              <p className="text-xs text-gray-500">
                Set the Dibal scale ECR interface to NCI or CAS, then match Protocol above.
              </p>
            )}
          </section>

          {/* Connection */}
          {needsSerial && (
            <section className="border border-gray-200 rounded-xl p-5 space-y-3">
              <h2 className="text-sm font-medium text-gray-900 pb-2 border-b border-gray-100">Connection</h2>
              {desktop ? (
                <label className="block">
                  <span className="text-xs text-gray-500">{t('settings.scalePort')}</span>
                  <select
                    className={`${SELECT_CLS} mt-1 font-mono`}
                    value={draft.path}
                    onChange={(e) => updateDraft('path', e.target.value)}
                  >
                    <option value="">—</option>
                    {ports.map((p) => (
                      <option key={p.id} value={p.path || p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="mt-2 text-xs text-gray-600 underline"
                    onClick={() => void listPorts().then(setPorts)}
                  >
                    Refresh ports
                  </button>
                </label>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-700">
                    Port:{' '}
                    <span className="font-mono text-xs">
                      {draft.portId || 'Not granted yet'}
                    </span>
                  </p>
                  <button
                    type="button"
                    disabled={!transport.available}
                    onClick={async () => {
                      const p = await requestWebPort();
                      if (p) {
                        showToast('Serial port granted.', true);
                        void listPorts().then(setPorts);
                      }
                    }}
                    className="text-sm px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                  >
                    {draft.portId ? 'Re-select port' : 'Grant serial port'}
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <label className="block">
                  <span className="text-xs text-gray-500">{t('settings.scaleBaud')}</span>
                  <select
                    className={`${SELECT_CLS} mt-1`}
                    value={draft.baudRate}
                    onChange={(e) => updateDraft('baudRate', Number(e.target.value))}
                  >
                    {[4800, 9600, 19200, 38400, 115200].map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500">Data bits</span>
                  <select
                    className={`${SELECT_CLS} mt-1`}
                    value={draft.dataBits}
                    onChange={(e) => updateDraft('dataBits', Number(e.target.value) as 7 | 8)}
                  >
                    <option value={7}>7</option>
                    <option value={8}>8</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500">Parity</span>
                  <select
                    className={`${SELECT_CLS} mt-1`}
                    value={draft.parity}
                    onChange={(e) =>
                      updateDraft('parity', e.target.value as 'none' | 'even' | 'odd')
                    }
                  >
                    <option value="none">None</option>
                    <option value="even">Even</option>
                    <option value="odd">Odd</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500">Stop bits</span>
                  <select
                    className={`${SELECT_CLS} mt-1`}
                    value={draft.stopBits}
                    onChange={(e) => updateDraft('stopBits', Number(e.target.value) as 1 | 2)}
                  >
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                  </select>
                </label>
              </div>
            </section>
          )}

          {/* Behaviour */}
          <section className="border border-gray-200 rounded-xl p-5">
            <h2 className="text-sm font-medium text-gray-900 pb-2 border-b border-gray-100 mb-1">
              Behaviour
            </h2>
            {(
              [
                ['autoConnect', 'Auto-connect on weight entry', 'Open the scale when the POS weight modal opens'],
                ['stableOnly', 'Stable weight only', 'Ignore unstable / in-motion readings when the protocol reports status'],
              ] as const
            ).map(([key, label, sub]) => (
              <div
                key={key}
                className="flex items-center justify-between py-3 border-b border-gray-100 last:border-none"
              >
                <div>
                  <p className="text-sm text-gray-800">{label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={draft[key]}
                  onClick={() => updateDraft(key, !draft[key])}
                  className={`relative w-10 h-6 rounded-full transition-colors ${
                    draft[key] ? 'bg-gray-900' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      draft[key] ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </section>

          {/* Test serial */}
          <section className="border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-medium text-gray-900">Test connection</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Live reading:{' '}
                  <span className="font-mono text-gray-800">
                    {lastWeightKg != null ? `${lastWeightKg.toFixed(3)} kg` : '—'}
                  </span>
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={testing || !transport.available || draft.brandPreset === 'none'}
                  onClick={() => void handleTest()}
                  className="text-sm px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 inline-flex items-center gap-2"
                >
                  {testing || connStatus === 'connecting' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Connecting…
                    </>
                  ) : (
                    'Test connection'
                  )}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await disconnect();
                    setLastWeightKg(null);
                  }}
                  className="text-sm px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Disconnect
                </button>
              </div>
            </div>
            {errorMsg && <p className="text-xs text-red-600 mt-2">{errorMsg}</p>}
          </section>
        </>
      )}

      {showBarcode && (
        <section className="border border-gray-200 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-medium text-gray-900 pb-2 border-b border-gray-100">
            {t('settings.scaleBarcode', { defaultValue: 'Scale Barcode' })}
          </h2>
          <label className="block">
            <span className="text-xs text-gray-500">
              {t('settings.barcodeFormat', { defaultValue: 'Barcode format' })}
            </span>
            <select className={`${SELECT_CLS} mt-1`} value="variable_weight_upc_ean" disabled>
              <option value="variable_weight_upc_ean">Variable-weight UPC/EAN</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-gray-500">
              {t('settings.weightUnit', { defaultValue: 'Weight unit' })}
            </span>
            <select
              className={`${SELECT_CLS} mt-1`}
              value={draft.scaleBarcodeProfile.weightUnit}
              onChange={(e) => updateDraftProfile('weightUnit', e.target.value as 'kg')}
            >
              <option value="kg">kg</option>
            </select>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-gray-300 text-gray-900"
              checked={draft.scaleBarcodeProfile.acceptLeadingEanZero}
              onChange={(e) => updateDraftProfile('acceptLeadingEanZero', e.target.checked)}
            />
            <span className="text-sm text-gray-800">
              {t('settings.acceptUpcEan', {
                defaultValue: 'Accept both UPC-A and EAN-13 representation',
              })}
            </span>
          </label>

          <button
            type="button"
            className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900"
            onClick={() => setAdvancedOpen((v) => !v)}
          >
            {advancedOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {t('settings.advancedSettings', { defaultValue: 'Advanced settings' })}
          </button>
          {advancedOpen && (
            <div className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 border border-gray-100 p-3">
              <label className="block">
                <span className="text-xs text-gray-500">Prefix</span>
                <input
                  className={`${SELECT_CLS} mt-1 font-mono`}
                  value={draft.scaleBarcodeProfile.prefix}
                  onChange={(e) => updateDraftProfile('prefix', e.target.value.replace(/\D/g, '').slice(0, 2))}
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">PLU digits</span>
                <input
                  type="number"
                  min={1}
                  max={8}
                  className={`${SELECT_CLS} mt-1`}
                  value={draft.scaleBarcodeProfile.pluLength}
                  onChange={(e) => updateDraftProfile('pluLength', Number(e.target.value) || 5)}
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">Weight digits</span>
                <input
                  type="number"
                  min={1}
                  max={8}
                  className={`${SELECT_CLS} mt-1`}
                  value={draft.scaleBarcodeProfile.valueLength}
                  onChange={(e) => updateDraftProfile('valueLength', Number(e.target.value) || 5)}
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">Weight decimal places</span>
                <input
                  type="number"
                  min={0}
                  max={6}
                  className={`${SELECT_CLS} mt-1`}
                  value={draft.scaleBarcodeProfile.weightDecimalPlaces}
                  onChange={(e) => updateDraftProfile('weightDecimalPlaces', Number(e.target.value) || 3)}
                />
              </label>
              <label className="block col-span-2">
                <span className="text-xs text-gray-500">Checksum validation</span>
                <select
                  className={`${SELECT_CLS} mt-1`}
                  value={draft.scaleBarcodeProfile.checksum}
                  onChange={(e) =>
                    updateDraftProfile('checksum', e.target.value as 'upc_ean' | 'none')
                  }
                >
                  <option value="upc_ean">UPC/EAN</option>
                  <option value="none">None</option>
                </select>
              </label>
            </div>
          )}

          <div className="border-t border-gray-100 pt-4 space-y-3">
            <p className="text-sm font-medium text-gray-900">
              {t('settings.testScaleBarcode', { defaultValue: 'Test scale barcode' })}
            </p>
            <p className="text-xs text-gray-500">
              Scan or paste a scale barcode. This does not add anything to the cart.
            </p>
            <div className="flex gap-2">
              <input
                className={`${SELECT_CLS} font-mono`}
                value={testCode}
                onChange={(e) => setTestCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleTestBarcode();
                  }
                }}
                placeholder="0277697006985"
              />
              <button
                type="button"
                disabled={testBusy || !testCode.trim()}
                onClick={() => void handleTestBarcode()}
                className="text-sm px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 shrink-0 inline-flex items-center gap-2"
              >
                {testBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Test
              </button>
            </div>
            {testResult.kind === 'invalid_checksum' && (
              <p className="text-sm text-red-600">✕ Invalid barcode checksum</p>
            )}
            {testResult.kind === 'not_recognized' && (
              <p className="text-sm text-amber-700">Barcode does not match the configured scale format.</p>
            )}
            {testResult.kind === 'ok' && (
              <div className="text-sm rounded-lg border border-gray-200 bg-white p-3 space-y-1">
                {testResult.unmapped ? (
                  <p className="text-green-700 font-medium">✓ Barcode format recognized</p>
                ) : testResult.productName ? (
                  <p className="text-green-700 font-medium">✓ Valid scale barcode</p>
                ) : (
                  <p className="text-amber-700 font-medium">✓ Format OK — lookup issue</p>
                )}
                <p>
                  <span className="text-gray-500">Product PLU</span>{' '}
                  <span className="font-mono">{testResult.plu}</span>
                </p>
                <p>
                  <span className="text-gray-500">Encoded weight</span>{' '}
                  {testResult.weight.toFixed(3)} {testResult.unit}
                </p>
                {testResult.productName ? (
                  <>
                    <p>
                      <span className="text-gray-500">Product</span> {testResult.productName}
                    </p>
                    <p>
                      <span className="text-gray-500">Current rate</span>{' '}
                      {formatMoney(testResult.rate || 0)} / {testResult.unit}
                    </p>
                    <p>
                      <span className="text-gray-500">Calculated amount</span>{' '}
                      {formatMoney(testResult.amount || 0)}
                    </p>
                  </>
                ) : (
                  <p className="text-amber-800">
                    {testResult.error
                      || `No product is assigned to this Scale PLU.`}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      <p className="text-xs text-gray-400">
        Printer settings moved?{' '}
        <Link to="/settings?tab=printer" className="underline text-gray-600">
          Open Printer
        </Link>
      </p>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={resetDefaults}
          className="text-sm text-gray-500 hover:text-gray-800"
        >
          Reset to defaults
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={discardDraft}
            disabled={!isDirty}
            className="text-sm px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty}
            className="text-sm px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40"
          >
            Save settings
          </button>
        </div>
      </div>
    </div>
  );
}
